<script module lang="ts">
  import type { Snippet } from 'svelte';
  import type { FullAutoFill, HTMLInputAttributes, HTMLTextareaAttributes } from 'svelte/elements';
  import type { AllowedInputType } from './forms.types';
  import type { IconType } from '../icons/icons.types';

  export interface Props extends Omit<HTMLInputAttributes, 'autocapitalize' | 'autocorrect' | 'autocomplete' | 'type' | 'value'> {
    /** Defaults to `input`, a plain text field; see forms.types.ts. */
    type?: AllowedInputType;
    value?: string | null;
    label?: string;
    htmlLabel?: Snippet;
    autocapitalize?: boolean;
    autocorrect?: boolean;
    /** Defaults to what the type suggests (`email`, `current-password`, `off`). */
    autocomplete?: FullAutoFill;
    /** Shown as a tooltip above the field while it has the focus. */
    focusHint?: string;
    /** The light icon at the end of the field; defaults to the one of the type (`@` for `email`), none for `input`. */
    icon?: IconType;
    /** A text of several lines: the field is a `<textarea>` with this height. */
    rows?: number;
    /** A `<textarea>` as high as its text, `rows` is then the least height; it never scrolls. */
    autoGrow?: boolean;
    /** As wide as the box around it instead of the usual form width. */
    wide?: boolean;
  }
</script>

<script lang="ts">
  import type { Attachment } from 'svelte/attachments';
  import { allowedInputTypes, inputTypeSettings } from './forms.types';
  import Tooltip from '../tooltips/Tooltip.svelte';
  import Icon from '../icons/Icon.svelte';

  let {
    type,
    label,
    htmlLabel,
    id,
    autocapitalize = false,
    autocorrect = false,
    autocomplete,
    autofocus,
    focusHint,
    icon,
    rows,
    autoGrow = false,
    wide = false,
    value = $bindable(),
    children,
    ...toNext
  }: Props = $props();

  // -
  const DefaultType = allowedInputTypes[0];
  const UseType = $derived(type && allowedInputTypes.includes(type) ? type : DefaultType);
  const Settings = $derived(inputTypeSettings[UseType]);
  const TypeIcon = $derived(icon ?? Settings.icon);

  // -
  const GeneratedId = $props.id();
  const InputId = $derived(id ?? GeneratedId);
  let InputElement: HTMLInputElement | HTMLTextAreaElement | undefined = $state();
  let FrameElement: HTMLDivElement | undefined = $state();
  let HasFocus = $state(false);
  const IsReadonly = $derived(Boolean(toNext.readonly));
  const TextareaProps = $derived(toNext as unknown as HTMLTextareaAttributes);

  $effect(() => {
    if (autofocus && InputElement && !IsReadonly) {
      const Element = InputElement;
      requestAnimationFrame(() => {
        Element.focus();
      });
    }
  });

  // - again when the width changes: the lines then wrap differently
  $effect(() => {
    const Element = InputElement;
    if (!autoGrow || Element === undefined || value === undefined) {
      return;
    }
    const fit = (): void => {
      Element.style.height = 'auto';
      Element.style.height = `${Element.scrollHeight.toString()}px`;
    };
    fit();
    let Width = Element.offsetWidth;
    const Observer = new ResizeObserver((): void => {
      if (Element.offsetWidth !== Width) {
        Width = Element.offsetWidth;
        fit();
      }
    });
    Observer.observe(Element);

    return (): void => {
      Observer.disconnect();
    };
  });

  /** A click anywhere in the frame focuses the input, the label lets clicks through. */
  const focusOnClick: Attachment<HTMLDivElement> = (element) => {
    const Handler = (event: MouseEvent): void => {
      if (!IsReadonly && event.target !== InputElement) {
        InputElement?.focus();
      }
    };

    const FocusIn = (): void => {
      HasFocus = true;
    };
    // - deferred: removing the focused field (closing a dialog, switching a page) fires focusout while Svelte
    //   updates the DOM, and setting state there throws `state_unsafe_mutation`
    const FocusOut = (): void => {
      queueMicrotask(() => {
        // - the focus may have moved to another element inside the frame in the meantime
        HasFocus = element.isConnected && element.contains(document.activeElement);
      });
    };

    element.addEventListener('click', Handler);
    element.addEventListener('focusin', FocusIn);
    element.addEventListener('focusout', FocusOut);
    return (): void => {
      element.removeEventListener('click', Handler);
      element.removeEventListener('focusin', FocusIn);
      element.removeEventListener('focusout', FocusOut);
    };
  };
</script>

<div
  class="formInput"
  class:isReadonly={IsReadonly}
  class:hasIcon={TypeIcon !== undefined}
  class:isWide={wide}
  class:isAutoGrow={autoGrow}
  bind:this={FrameElement}
  {@attach focusOnClick}>
  {#if rows === undefined}
    <!--suppress HtmlDeprecatedAttribute -->
    <input
      {...toNext}
      id={InputId}
      type={Settings.htmlType}
      placeholder="&nbsp;"
      bind:value
      bind:this={InputElement}
      autocomplete={autocomplete ?? Settings.autocomplete}
      inputmode={Settings.inputmode}
      spellcheck={Settings.spellcheck}
      autocapitalize={autocapitalize ? 'on' : 'off'}
      autocorrect={autocorrect ? 'on' : 'off'} />
  {:else}
    <textarea {...TextareaProps} id={InputId} {rows} placeholder="&nbsp;" bind:value bind:this={InputElement} spellcheck={Settings.spellcheck}></textarea>
  {/if}
  {#if label || htmlLabel}
    <label for={InputId}>
      {label}
      {@render htmlLabel?.()}
    </label>
  {/if}
  {#if TypeIcon}
    <span class="inputIcon" aria-hidden="true"><Icon icon={TypeIcon} /></span>
  {/if}
  {@render children?.()}
  {#if focusHint && FrameElement}
    <Tooltip text={focusHint} reference={FrameElement} visible={HasFocus && !IsReadonly} triggerOnHover={false} />
  {/if}
</div>

<style lang="scss">
  @use '../../../styles/display';

  .formInput {
    @include display.block;
    position: relative;
    font-size: var(--font-size);
    background: var(--layout-background);
    border: 1px solid var(--layout-border-hard-color);
    color: var(--layout-font-color);
    max-width: 30em;
    --top-abs: calc(var(--font-size) * 0.6);
    padding: 0.25em 0.5em;
    border-radius: 0.5em;

    + :global(.formInput) {
      margin-top: 1rem;
    }

    &.isWide {
      max-width: none;
    }

    &.isAutoGrow > textarea {
      resize: none;
      overflow: hidden;
    }

    // - the kind of the field, light at its end; the input and the label leave room for it
    &.hasIcon {
      > input,
      > textarea {
        padding-right: 1.75em;
      }

      > label {
        right: 2rem;
      }
    }

    .inputIcon {
      position: absolute;
      z-index: 15;
      top: 50%;
      right: 0.6em;
      transform: translateY(-50%);
      opacity: 0.45;
      pointer-events: none;
    }

    // - shown, not editable: no events reach the input
    &.isReadonly {
      cursor: default;
      opacity: 0.8;
      background: var(--layout-mark-bg-inactive);

      > input,
      > textarea {
        pointer-events: none;
        background: transparent;
      }
    }

    > input {
      line-height: 1;

      // - the browser's own date icon stays clickable, but invisible over ours
      &::-webkit-calendar-picker-indicator {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        width: 1.75em;
        height: auto;
        margin: 0;
        padding: 0;
        opacity: 0;
        cursor: pointer;
      }
    }

    > textarea {
      line-height: 1.3;
      resize: vertical;
    }

    > input,
    > textarea {
      @include display.block;
      position: relative;
      z-index: 10;
      font-size: 0.9em !important;
      margin-top: calc(var(--top-abs) * 1.125);
      width: 100%;
      box-sizing: border-box;

      &,
      &:focus,
      &:hover {
        outline: none;
        border: none;
      }

      &:not(:focus):placeholder-shown {
        + label {
          font-size: 1.25em;
          top: 0.35em;
          opacity: 0.5;
          font-style: italic;
        }
      }
    }

    > label {
      @include display.block;
      position: absolute;
      z-index: 20;
      top: 0.125em;
      left: 0.5rem;
      right: 0.5rem;
      font-size: var(--top-abs);
      opacity: 0.9;
      pointer-events: none;
      transition: all 0.25s ease-in-out;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
</style>
